import { type IntentContext } from '../../types';
import { MIN_RECENT_PREFIX_TOKENS, MIN_RECENT_REORDERED_TOKENS } from '../pack/constants';
import { type RecentMatch } from '../types';
import { normalizeQuery } from './normalize';
import { getContentTokens } from './operand-evidence';

/**
 * `recentMatch`: how the query relates to the titles the user recently viewed.
 *
 * SOURCE: "Search x Chat Convergence - MVP Triggering Architecture", `S-RECENT-NAV` (980).
 * https://hello.atlassian.net/wiki/spaces/jirasearch/pages/7525895377
 *
 * Both sides are compared as **content tokens**: NFKC-normalized, segmented by
 * `Intl.Segmenter`, case-folded, with `STOP_WORDS_V1` and connectors removed. That single
 * choice is what makes `the launch plan`, `Launch Plan` and `launch-plan` one title, and it
 * is why emoji and punctuation in a title never get in the way: the segmenter does not
 * report them as word-like. Bracketed decorations such as `[DRAFT]` or `(WIP)` are stripped
 * from titles before indexing.
 *
 * Three relations, strongest first:
 *
 * - `EXACT`: the query's tokens are a title's tokens, in order.
 * - `PREFIX`: at least `MIN_RECENT_PREFIX_TOKENS` query tokens lead a title's tokens. The
 *   last query token may still be being typed: `launch plan temp` for `Launch plan template`.
 * - `REORDERED`: at least `MIN_RECENT_REORDERED_TOKENS` query tokens are a title's tokens in
 *   another order. Stricter than prefix, because word order is the weaker signal.
 *
 * A subset of a title is never a match: `roadmap planning` against `Q3 roadmap planning
 * notes` is left to the rules below.
 *
 * The title index is cached by the identity of the `recentActivities` array, which the
 * surface replaces only when its recents refresh, so a snapshot of titles is tokenized once
 * rather than on every keystroke.
 */

type RecentActivities = NonNullable<IntentContext['recentActivities']>;

/** `[DRAFT]`, `(WIP)` and similar bracketed decorations in a title. */
const BRACKETED_DECORATION = /\[[^\]]*\]|\([^)]*\)/gu;

const toContentTokens = (text: string): readonly string[] =>
	getContentTokens(text).map((token) => token.toLowerCase());

/** The content tokens a recently viewed title is matched by. */
export const indexTitle = (title: string): readonly string[] =>
	toContentTokens(normalizeQuery(title.replace(BRACKETED_DECORATION, ' ')));

const indexCache = new WeakMap<RecentActivities, readonly (readonly string[])[]>();

const getRecentIndex = (recentActivities: RecentActivities): readonly (readonly string[])[] => {
	let index = indexCache.get(recentActivities);

	if (!index) {
		index = recentActivities
			.map(({ title }) => indexTitle(title))
			.filter((tokens) => tokens.length > 0);
		indexCache.set(recentActivities, index);
	}

	return index;
};

const isExact = (query: readonly string[], title: readonly string[]): boolean =>
	query.length === title.length && query.every((token, index) => token === title[index]);

const isPrefix = (query: readonly string[], title: readonly string[]): boolean => {
	const last = query.length - 1;

	return (
		query.length >= MIN_RECENT_PREFIX_TOKENS &&
		title.length >= query.length &&
		query.every((token, index) =>
			index === last ? title[index].startsWith(token) : token === title[index],
		)
	);
};

const sortedKey = (tokens: readonly string[]): string => [...tokens].sort().join(' ');

export const resolveRecentMatch = (
	qMatch: string,
	recentActivities: RecentActivities | undefined,
): RecentMatch => {
	if (!recentActivities || recentActivities.length === 0) {
		return 'NONE';
	}

	const query = toContentTokens(qMatch);

	if (query.length === 0) {
		return 'NONE';
	}

	const canReorder = query.length >= MIN_RECENT_REORDERED_TOKENS;
	const queryKey = canReorder ? sortedKey(query) : '';
	let best: RecentMatch = 'NONE';

	for (const title of getRecentIndex(recentActivities)) {
		if (isExact(query, title)) {
			return 'EXACT';
		}

		if (isPrefix(query, title)) {
			best = 'PREFIX';
		} else if (
			best === 'NONE' &&
			canReorder &&
			title.length === query.length &&
			sortedKey(title) === queryKey
		) {
			best = 'REORDERED';
		}
	}

	return best;
};
